import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useUser } from '@clerk/clerk-react';
import { 
  Send, 
  Paperclip, 
  X, 
  File, 
  Plus, 
  Contact2, 
  Calendar,
  Mail
} from 'lucide-react';

import {
  CONTACTS_SCOPE,
  CALENDAR_SCOPE,
  computeGrantedFromUser,
  computeMicrosoftMailState,
  autoFinalizeMicrosoftMailboxIfGranted,
  handleConnectScope,
  handleConnectMicrosoftMailbox,
  handleConnectMicrosoftMailSend,
  handleConnectMicrosoftCalendar,
  handleDisconnectScope
} from '../../utils/authUtils';
import {
  findClerkExternalAccount,
  MICROSOFT_CALENDAR_SCOPE,
} from 'clerk-provider-config';
import { getMailIntegrationStatusService } from '../../utils/userService';
import ChatUnavailableNotice from '../Shared/ChatUnavailableNotice';

const CALENDAR_PROVIDER_LABELS = Object.freeze({
  google: 'Google Calendar',
  microsoft: 'Microsoft Calendar',
});

const MICROSOFT_CALENDAR_RECOVERY = Object.freeze({
  CALENDAR_OAUTH_MISSING: Object.freeze({
    message: 'Microsoft Calendar wasn’t connected. Approve Calendar access to continue.',
    action: 'Try again',
  }),
  CALENDAR_CONSENT_DECLINED: Object.freeze({
    message: 'Microsoft Calendar wasn’t connected. Approve Calendar access to continue.',
    action: 'Try again',
  }),
  CALENDAR_TOKEN_EXPIRED: Object.freeze({
    message: 'Microsoft Calendar needs to be reconnected.',
    action: 'Reconnect Microsoft Calendar',
  }),
  CALENDAR_ADMIN_CONSENT_REQUIRED: Object.freeze({
    message: 'Microsoft Calendar access needs administrator approval. Ask your Microsoft 365 administrator to approve access, then try again.',
    action: 'Try again',
  }),
  CALENDAR_RATE_LIMITED: Object.freeze({
    message: 'Microsoft Calendar is temporarily busy. Try again in a moment.',
    action: 'Try again',
  }),
});

const GENERIC_CALENDAR_RECOVERY = Object.freeze({
  message: 'We couldn’t connect Calendar. Try again.',
  action: 'Try again',
});

export default function ChatBox({
  onSendMessage,
  resetChatBoxInput,
  setResetChatBoxInput,
  restoredChatBoxInput,
  setRestoredChatBoxInput,
  disabled,
  showChatContainer,
  overrideRows = 2,
  overridePosition,
  overridePlaceholder,
  ampSection
}) {
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const fileInputRef = useRef(null);

  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [calendarGranted, setCalendarGranted] = useState(false);
  const [contactsGranted, setContactsGranted] = useState(false);
  const [microsoftCalendarConfigured, setMicrosoftCalendarConfigured] = useState(false);
  const [microsoftCalendarGranted, setMicrosoftCalendarGranted] = useState(false);
  const [microsoftCalendarErrorCode, setMicrosoftCalendarErrorCode] = useState(null);
  const [microsoftMailboxGranted, setMicrosoftMailboxGranted] = useState(false);
  const [microsoftMailboxConnected, setMicrosoftMailboxConnected] = useState(false);
  const [microsoftProviderAccountId, setMicrosoftProviderAccountId] = useState(null);
  const [microsoftMailSendGranted, setMicrosoftMailSendGranted] = useState(false);
  const [microsoftMailErrorCode, setMicrosoftMailErrorCode] = useState(null);

  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const featureToggleInFlightRef = useRef(false);
  const featureOpenerIdRef = useRef(null);
  const closeFeatureButtonRef = useRef(null);
  const featureDialogRef = useRef(null);
  const featureDialogHeadingId = React.useId();

  const { user, isLoaded } = useUser();

  // Every chat surface renders through this component, so gating here is what
  // makes the unavailable state consistent instead of five separate notices.
  // `chatAvailable` is false only once the server has actually said so --
  // null means "not answered yet", which must not read as broken.
  const chatUnavailable = useSelector((state) => state.assistant?.chatAvailable === false);
  const chatUnavailableReason = useSelector((state) => state.assistant?.degradedReason);

  const hasGoogleAccount = Boolean(isLoaded && user && findClerkExternalAccount(user, 'google'));
  const hasMicrosoftAccount = Boolean(isLoaded && user && findClerkExternalAccount(user, 'microsoft'));

  useEffect(() => {
    if (!isLoaded) return;

    if (!user) {
      setContactsGranted(false);
      setCalendarGranted(false);
      setMicrosoftCalendarConfigured(false);
      setMicrosoftCalendarGranted(false);
      setMicrosoftCalendarErrorCode(null);
      setMicrosoftMailboxGranted(false);
      setMicrosoftMailboxConnected(false);
      setMicrosoftProviderAccountId(null);
      setMicrosoftMailSendGranted(false);
      setMicrosoftMailErrorCode(null);
      return;
    }

    const fetchGranted = async () => {
      setMicrosoftCalendarConfigured(
        (user.publicMetadata?.additionalScopes ?? []).includes(MICROSOFT_CALENDAR_SCOPE)
      );
      const backendStatus = await getMailIntegrationStatusService('microsoft').catch(() => null);
      const providerAccountId = backendStatus?.providerAccountId ?? null;
      setMicrosoftProviderAccountId(providerAccountId);
      try {
        const {
          contactsGranted,
          calendarGranted,
          microsoftCalendarGranted,
        } = await computeGrantedFromUser(user, providerAccountId);
        setContactsGranted(contactsGranted);
        setCalendarGranted(calendarGranted);
        setMicrosoftCalendarGranted(microsoftCalendarGranted);
      } catch (error) {
        console.error('Error resolving connected feature scopes:', error);
      }

      try {
        const microsoftMailState = await computeMicrosoftMailState(user, providerAccountId);
        setMicrosoftMailboxGranted(microsoftMailState.microsoftMailboxGranted);
        setMicrosoftMailSendGranted(microsoftMailState.microsoftMailSendGranted);

        await autoFinalizeMicrosoftMailboxIfGranted({
          state: microsoftMailState,
          setMicrosoftMailboxGranted,
          setMicrosoftMailSendGranted,
          setMicrosoftMailErrorCode,
          getStatusFn: async () => backendStatus,
        });

        setMicrosoftMailboxConnected(Boolean(
          microsoftMailState.microsoftMailboxGranted && backendStatus?.connected
        ));
      } catch (error) {
        console.error('Error finalizing the Microsoft mailbox connection:', error);
        setMicrosoftMailboxConnected(false);
      }
    }

    fetchGranted();
  }, [user, isLoaded]);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (resetChatBoxInput) {
      setMessage('');
      setFile(null);
      setShowFileDialog(false);
      setResetChatBoxInput(false);
    }
  }, [resetChatBoxInput, setResetChatBoxInput]);

  // A failed turn hands the message back. The transcript bubble is not
  // editable, so this is the only route the user has to their own text --
  // retyping it is the alternative.
  useEffect(() => {
    if (!restoredChatBoxInput) return;
    setMessage(restoredChatBoxInput.message ?? '');
    setFile(restoredChatBoxInput.file ?? null);
    setRestoredChatBoxInput?.(null);
  }, [restoredChatBoxInput, setRestoredChatBoxInput]);

  const sendMessage = () => {
    if (!message.trim()) return;

    if (file) {
      const allowedTypes = [
        'text/plain',
        'text/markdown',
        'application/json',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/csv',
        'text/tab-separated-values',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!allowedTypes.includes(file.type)) {
        alert('File type not allowed!');
        return;
      }
    }

    if (chatUnavailable) return;

    onSendMessage(message, file);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && e.target.value.trim() !== "") {
      sendMessage();
    }
  };

  const calculateRows = () => {
    const lineCount = (message.match(/\n/g) || []).length + 1;
    return Math.min(Math.max(overrideRows, lineCount), 10);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setShowFileDialog(true);
    }
    e.target.value = '';
  };

  const handleRemoveFile = () => {
    setFile(null);
    setShowFileDialog(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const FEATURES = [
    // TODO: Re-enable this once Contacts are fully implemented
    // {
    //   id: 'contacts',
    //   scope: CONTACTS_SCOPE,
    //   title: 'Connect Google Contacts',
    //   description: 'Find people and email addresses faster',
    //   icon: Contact2,
    //   fullDescription:
    //     'After you connect Google Contacts, Geoffrey can look up people by name, match names to email addresses, suggest recipients, and pull contact details into your conversations. You can disconnect anytime.',
    // },
    {
      id: 'calendar',
      provider: 'google',
      scope: CALENDAR_SCOPE,
      title: 'Connect Google Calendar',
      description: 'Check availability and schedule meetings from chat',
      icon: Calendar,
      fullDescription:
        'After you connect Google Calendar, Geoffrey can view your availability, create events, update or reschedule meetings, and add details like location or conferencing links—right from the conversation. You can disconnect anytime.',
    },
    {
      id: 'microsoft_calendar',
      provider: 'microsoft',
      scope: MICROSOFT_CALENDAR_SCOPE,
      title: 'Connect Microsoft Calendar',
      description: 'Check availability and schedule meetings from chat',
      icon: Calendar,
      fullDescription:
        'After you connect Microsoft Calendar, Geoffrey can view your availability, create events, update or reschedule meetings, and add details like location or attendees—right from the conversation. You can disable access anytime.',
    },
    {
      id: 'microsoft_contacts',
      provider: 'microsoft',
      title: 'Microsoft Contacts',
      description: 'Coming soon',
      icon: Contact2,
      comingSoon: true,
    },
    {
      id: 'microsoft_mailbox',
      provider: 'microsoft',
      microsoftAction: 'mailbox',
      title: 'Connect Microsoft Mailbox',
      description: 'Read and organize your Outlook mail from chat',
      icon: Mail,
      fullDescription:
        'Connect your Microsoft mailbox to let Geoffrey read and organize your Outlook mail. Your Microsoft identity sign-in stays separate from this mailbox permission.',
    },
    {
      id: 'microsoft_mail_send',
      provider: 'microsoft',
      microsoftAction: 'send',
      title: 'Allow Microsoft Email Sending',
      description: 'Explicitly allow sending approved Outlook drafts',
      icon: Send,
      fullDescription:
        'Allow sending Microsoft email as a separate permission. This permission is never implied by connecting your mailbox.',
    },
  ].filter((feature) => {
    if (feature.provider === 'google') return hasGoogleAccount;
    if (feature.id === 'microsoft_calendar') {
      return hasMicrosoftAccount || microsoftCalendarConfigured;
    }
    if (feature.provider === 'microsoft') return hasMicrosoftAccount;
    return true;
  });

  const openFeatureModal = (feature) => {
    featureOpenerIdRef.current = feature.id;
    setSelectedFeature(feature);
    if (feature.scope === MICROSOFT_CALENDAR_SCOPE) {
      setMicrosoftCalendarErrorCode(null);
    }
    setShowPopup(false);
  };

  const closeFeatureModal = () => {
    const openerId = featureOpenerIdRef.current;
    setSelectedFeature(null);
    setShowPopup(true);
    window.setTimeout(() => {
      const opener = Array.from(
        popupRef.current?.querySelectorAll('[data-feature-id]') ?? []
      ).find((element) => element.dataset.featureId === openerId);
      opener?.focus();
    }, 0);
  };

  useEffect(() => {
    if (!selectedFeature) return undefined;

    const hiddenBackground = [];
    let dialogPathNode = featureDialogRef.current;
    while (dialogPathNode?.parentElement) {
      const parent = dialogPathNode.parentElement;
      for (const sibling of parent.children) {
        if (sibling === dialogPathNode || sibling.hasAttribute('data-modal-backdrop')) continue;
        hiddenBackground.push({
          element: sibling,
          ariaHidden: sibling.getAttribute('aria-hidden'),
          wasInert: sibling.hasAttribute('inert'),
        });
        sibling.setAttribute('inert', '');
        sibling.setAttribute('aria-hidden', 'true');
      }
      if (parent === document.body) break;
      dialogPathNode = parent;
    }
    closeFeatureButtonRef.current?.focus();
    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') closeFeatureModal();
      if (event.key !== 'Tab') return;

      const focusable = Array.from(featureDialogRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []).filter((element) => element.getAttribute('aria-hidden') !== 'true');
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;
      if (!featureDialogRef.current?.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown);
      for (const { element, ariaHidden, wasInert } of hiddenBackground) {
        if (!wasInert) element.removeAttribute('inert');
        if (ariaHidden === null) {
          element.removeAttribute('aria-hidden');
        } else {
          element.setAttribute('aria-hidden', ariaHidden);
        }
      }
    };
  }, [selectedFeature]);

  const getFeatureGranted = (feature) => {
    if (feature.comingSoon) return false;
    if (feature.microsoftAction === 'mailbox') {
      return microsoftMailboxGranted && microsoftMailboxConnected;
    }
    if (feature.microsoftAction === 'send') return microsoftMailSendGranted;
    if (feature.scope === MICROSOFT_CALENDAR_SCOPE) {
      return microsoftCalendarGranted && microsoftMailboxConnected;
    }
    return feature.scope === CONTACTS_SCOPE ? contactsGranted : calendarGranted;
  };

  const handleFeatureToggle = async () => {
    if (!selectedFeature || featureToggleInFlightRef.current) return;
    featureToggleInFlightRef.current = true;

    try {

      if (selectedFeature.microsoftAction === 'mailbox') {
        await handleConnectMicrosoftMailbox({
          user,
          setIsAuthorizing,
          setMicrosoftMailboxGranted,
          setMicrosoftMailSendGranted,
          setMicrosoftMailErrorCode,
        });

        const backendStatus = await getMailIntegrationStatusService('microsoft').catch(() => null);
        const providerAccountId = backendStatus?.providerAccountId ?? null;
        setMicrosoftProviderAccountId(providerAccountId);
        const microsoftMailState = await computeMicrosoftMailState(user, providerAccountId);
        setMicrosoftMailboxGranted(microsoftMailState.microsoftMailboxGranted);
        setMicrosoftMailSendGranted(microsoftMailState.microsoftMailSendGranted);
        setMicrosoftMailboxConnected(Boolean(
          microsoftMailState.microsoftMailboxGranted && backendStatus?.connected
        ));
        return;
      }

      if (selectedFeature.microsoftAction === 'send') {
        await handleConnectMicrosoftMailSend({
          user,
          setIsAuthorizing,
          setMicrosoftMailboxGranted,
          setMicrosoftMailSendGranted,
          setMicrosoftMailErrorCode,
        });
        return;
      }

      const granted = getFeatureGranted(selectedFeature);
      const selectedMicrosoftCalendarConfigured = selectedFeature.scope === MICROSOFT_CALENDAR_SCOPE
        && microsoftCalendarConfigured;

      if (selectedMicrosoftCalendarConfigured) {
        await handleDisconnectScope({
          scope: selectedFeature.scope,
          setContactsGranted,
          setCalendarGranted,
          setMicrosoftCalendarGranted: (granted) => {
            setMicrosoftCalendarConfigured(granted);
            setMicrosoftCalendarGranted(granted);
          },
        });
        setMicrosoftCalendarErrorCode(null);
        return;
      }

      if (
        selectedFeature.scope === MICROSOFT_CALENDAR_SCOPE
        && !microsoftMailboxConnected
      ) {
        const mailboxFeature = FEATURES.find(({ microsoftAction }) => microsoftAction === 'mailbox');
        if (mailboxFeature) openFeatureModal(mailboxFeature);
        return;
      }

      if (selectedFeature.scope === MICROSOFT_CALENDAR_SCOPE && !granted) {
        await handleConnectMicrosoftCalendar({
          user,
          providerAccountId: microsoftProviderAccountId,
          setIsAuthorizing,
          setMicrosoftCalendarGranted: (granted) => {
            setMicrosoftCalendarConfigured(granted);
            setMicrosoftCalendarGranted(granted);
          },
          setMicrosoftCalendarErrorCode,
        });
        return;
      }

      if (granted) {
        await handleDisconnectScope({
          scope: selectedFeature.scope,
          setContactsGranted,
          setCalendarGranted,
          setMicrosoftCalendarGranted,
        });
        if (selectedFeature.scope === MICROSOFT_CALENDAR_SCOPE) {
          setMicrosoftCalendarErrorCode(null);
        }
      } else {
        await handleConnectScope({
          scope: selectedFeature.scope,
          user,
          setIsAuthorizing,
          setContactsGranted,
          setCalendarGranted,
        });
      }
    } finally {
      featureToggleInFlightRef.current = false;
    }
  };

  const renderFeatureToggle = () => {
    const granted = getFeatureGranted(selectedFeature);
    const isMicrosoftCalendar = selectedFeature.scope === MICROSOFT_CALENDAR_SCOPE;
    if (isMicrosoftCalendar) {
      const providerLabel = CALENDAR_PROVIDER_LABELS[selectedFeature.provider] ?? 'Calendar';
      const configured = microsoftCalendarConfigured;
      const noAccount = microsoftCalendarErrorCode === 'CALENDAR_AUTH_REQUIRED';
      const mailboxRequired = !configured && !microsoftMailboxConnected;
      const mailboxDisconnected = configured && !microsoftMailboxConnected;
      const recovery = mailboxDisconnected
        ? {
          message: 'Calendar access is enabled, but the Microsoft mailbox is disconnected.',
          action: null,
        }
        : mailboxRequired
        ? {
          message: 'Connect your Microsoft mailbox before enabling Calendar.',
          action: 'Connect Microsoft Mailbox',
        }
        : noAccount
        ? { message: 'No Microsoft account is connected.', action: null }
        : microsoftCalendarErrorCode
          ? MICROSOFT_CALENDAR_RECOVERY[microsoftCalendarErrorCode]
            ?? GENERIC_CALENDAR_RECOVERY
          : null;
      const status = isAuthorizing
        ? 'Connecting Microsoft Calendar…'
        : mailboxDisconnected
          ? 'Access enabled; mailbox disconnected'
          : mailboxRequired ? 'Mailbox required' : granted ? 'Connected' : 'Disconnected';
      const action = isAuthorizing
        ? 'Connecting Microsoft Calendar…'
        : configured
          ? 'Disable access'
          : mailboxRequired
          ? 'Connect Microsoft Mailbox'
          : recovery?.action ?? 'Connect Microsoft Calendar';
      const accessibleName = configured
        ? granted
          ? `${providerLabel} connected. ${action}`
          : `${providerLabel} access enabled. ${action}`
        : mailboxRequired
        ? `${providerLabel} unavailable. ${action}`
        : granted
        ? `${providerLabel} connected. ${action}`
        : `${providerLabel} disconnected. ${action}`;

      return (
        <div className="flex flex-col items-end gap-2 min-w-0">
          <span
            role="status"
            aria-live="polite"
            className={`text-sm font-medium ${configured ? 'text-green-600' : 'text-gray-500'}`}
          >
            {status}
          </span>

          {action && !noAccount && (
            <button
              type="button"
              disabled={isAuthorizing}
              aria-busy={isAuthorizing ? 'true' : undefined}
              aria-pressed={configured}
              aria-label={accessibleName}
              onClick={handleFeatureToggle}
              className="min-h-[44px] px-4 py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold whitespace-normal break-words focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {action}
            </button>
          )}

          {recovery?.message && (
            <p
              role="alert"
              className="max-w-xs text-right text-sm leading-relaxed text-red-700 break-words"
            >
              {recovery.message}
            </p>
          )}
        </div>
      );
    }

    const isMicrosoftMailbox = selectedFeature.microsoftAction === 'mailbox';
    const isMicrosoftSend = selectedFeature.microsoftAction === 'send';
    const isMicrosoftFeature = isMicrosoftMailbox || isMicrosoftSend;
    const isDisabled =
      isAuthorizing ||
      (isMicrosoftMailbox && microsoftMailboxConnected) ||
      (isMicrosoftSend && (granted || !microsoftMailboxConnected));
    const errorMessage = !isMicrosoftFeature
      ? null
      : microsoftMailErrorCode === 'mail_admin_consent_required'
        ? 'Your Microsoft 365 administrator must approve mailbox access before you can connect.'
        : isMicrosoftSend && !microsoftMailboxConnected
          ? 'Connect your Microsoft mailbox before enabling email sending.'
          : microsoftMailErrorCode
            ? 'Microsoft mail could not be connected. Please try again.'
            : null;

    return (
      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-3">
          <span
            className={`
              text-sm font-medium ${granted ? 'text-green-600' : 'text-gray-500'}
            `}
          >
            {granted ? 'Connected' : 'Disconnected'}
          </span>

          <button
            disabled={isDisabled}
            onClick={handleFeatureToggle}
            className={`
              w-12 h-6 p-0.5 rounded-full transition-colors duration-200
              flex items-center ${isDisabled && !granted ? 'opacity-50 cursor-not-allowed' : ''}
              ${granted ? 'bg-sky-500' : 'bg-gray-300'}
            `}
          >
            <div
              className={`
                w-5 h-5 bg-white rounded-full transition-transform duration-200
                ${granted ? 'translate-x-6' : 'translate-x-0'}
              `}
            />
          </button>
        </div>
        {errorMessage && (
          <p className="max-w-xs text-right text-xs text-gray-500">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="relative">

      <div className={`w-full ${overridePosition ? overridePosition : "absolute bottom-0 left-0 right-0"}`}>

        {/* Disabling the composer without saying why just looks broken, so the
            notice and the disabled state always ship together. */}
        {chatUnavailable && <ChatUnavailableNotice reason={chatUnavailableReason} className="mb-2" />}

        {/* Wrapper for textarea + action bar */}
        <div className={`w-full bg-white border border-gray-200 rounded-lg shadow-sm mb-3`}>

          <div className="p-3 border-b border-gray-200 relative">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-amp-track-section={ampSection}
              data-amp-track-action="Chat_Input_Change"
              data-amp-track-description="Chat Input Change"
              disabled={disabled || !showChatContainer || chatUnavailable}
              placeholder={chatUnavailable
                ? "Chat is unavailable right now"
                : (overridePlaceholder ? overridePlaceholder : "How can I help you?")}
              className="min-h-[35px] w-full resize-none text-sm outline-none bg-transparent"
              rows={calculateRows()}
              onKeyDown={handleKeyDown}
            />

            {showFileDialog && file && (
              <div className="absolute left-1 max-w-[90%] bottom-full mb-2 flex items-center bg-gray-100 px-3 py-2 border border-solid border-b-slate-400 border-r-slate-400 rounded shadow z-10">
                <p className="truncate flex items-center gap-2">
                  <File size={24} className="text-lime-700" />
                  <span className="text-sm font-semibold text-stone-700">{file.name}</span>
                </p>
                <button
                  onClick={handleRemoveFile}
                  className="ml-3 text-black px-2 py-1 font-bold bg-transparent border-none hover:bg-gray-200 rounded-lg"
                >
                  <X size={18} />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 p-3">

            <label className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
              <Paperclip size={20} />
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt, .md, .markdown, .json, .jsonl, .pdf, .docx, .csv, .tsv, .xlsx"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>

            <div className="relative" ref={popupRef}>
              { 
                <button
                  onClick={() => setShowPopup(!showPopup)}
                  className="min-w-[44px] min-h-[44px] p-2 text-gray-600 bg-transparent hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  aria-haspopup="dialog"
                  aria-expanded={showPopup}
                >
                  <Plus size={20} />
                </button>
              }
              
              {showPopup && (
                <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-64 max-h-[70vh] overflow-y-auto overflow-x-hidden">
                  {FEATURES.map((f) => {
                       const granted = getFeatureGranted(f);
                       return (
                        <button
                          key={f.id}
                          onClick={f.comingSoon ? undefined : () => openFeatureModal(f)}
                          disabled={f.comingSoon}
                          data-feature-id={f.id}
                          className={`
                            w-full min-h-[44px] text-left px-4 py-3
                            flex items-start gap-3
                            bg-transparent
                            ${f.comingSoon ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"}
                          `}
                        >
                          {/* GREEN DOT */}
                          <div className="relative flex-shrink-0">
                            <f.icon size={20} className="text-blue-600 mt-0.5" />

                              <span
                                className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 ${granted ? "bg-green-500" : "bg-gray-300"} rounded-full ring-2 ring-white`}
                              />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 text-sm">
                              {f.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {f.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            <div className="flex-1" />

            <button
              className={`p-2 rounded-lg transition-colors text-white bg-sky-500 hover:bg-sky-600 ${disabled || !showChatContainer || chatUnavailable || message.trim().length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={disabled || !showChatContainer || chatUnavailable || message.trim().length === 0}
              onClick={sendMessage}
              data-amp-track-section={ampSection}
              data-amp-track-action="Chat_Input_Send"
              data-amp-track-description="Chat Input Send"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>

      {selectedFeature && (
        <>
          <div
            onClick={closeFeatureModal}
            data-testid="calendar-dialog-backdrop"
            data-modal-backdrop=""
            aria-hidden="true"
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          />

          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div
              ref={featureDialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={featureDialogHeadingId}
              className="bg-white rounded-lg shadow-xl max-w-md w-full relative overflow-x-hidden"
            >

              <button
                ref={closeFeatureButtonRef}
                type="button"
                onClick={closeFeatureModal}
                aria-label="Close calendar connection"
                className="absolute top-2 right-2 min-w-[44px] min-h-[44px] p-2 flex items-center justify-center rounded bg-transparent hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
              >
                <X size={18} className="text-gray-500" />
              </button>

              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start gap-3">
                  {selectedFeature.icon && (
                    <selectedFeature.icon size={28} className="text-blue-600" />
                  )}

                  <h2 id={featureDialogHeadingId} className="text-xl font-semibold text-gray-800 mb-0 pr-8">
                    {selectedFeature.title}
                  </h2>
                </div>

                {selectedFeature.description && (
                  <p className="text-sm text-gray-500 mt-2 ml-[40px]">
                    {selectedFeature.description}
                  </p>
                )}
              </div>

              <div className="px-6 pb-6">
                <div
                  data-testid="calendar-feature-detail"
                  className="border border-gray-200 rounded-lg p-4 mt-6 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 overflow-x-hidden"
                >
                  <p className="text-gray-600 text-sm leading-relaxed min-w-0 md:max-w-[70%] break-words">
                    {selectedFeature.fullDescription}
                  </p>

                  {renderFeatureToggle()}
                </div>
              </div>

            </div>
          </div>
        </>
      )}
    </div>
  );
}
